import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { categories, hotComics, latestUpdates, rankingComics } from '@/mocks/comics';

export default function Home() {
  const [activeCategoryTab, setActiveCategoryTab] = useState('all');
  const [scrollToTopVisible, setScrollToTopVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollToTopVisible(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredHotComics = activeCategoryTab === 'all'
    ? hotComics
    : hotComics.filter((c) => c.categorySlug === activeCategoryTab);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Hero Section */}
        <section
          className="relative w-full min-h-[620px] flex items-center justify-center overflow-hidden"
          aria-labelledby="hero-heading"
        >
          <img
            src="https://readdy.ai/api/search-image?query=Japanese%20anime%20manga%20style%20beautiful%20illustration%20with%20vibrant%20warm%20colors%2C%20magical%20fantasy%20world%20with%20floating%20books%20and%20stars%2C%20dreamy%20atmosphere%2C%20soft%20warm%20golden%20and%20coral%20tones%2C%20artistic%20illustration%20with%20flowing%20energy%2C%20no%20text%2C%20abstract%20ethereal%20background%20with%20sparkles%20and%20light%20particles&width=1920&height=900&seq=hero-bg-2026&orientation=landscape"
            alt="妖精漫画网 - 免费漫画大全"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/55"></div>

          <div className="relative z-10 w-full max-w-[860px] mx-auto px-4 md:px-6 text-center">
            <p className="text-accent-400 text-sm md:text-base font-medium tracking-widest mb-4 animate-fade-in">
              每日更新 · 完全免费 · 高清画质
            </p>
            <h1 id="hero-heading" className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-5 leading-tight">
              妖精漫画网
              <span className="block text-xl md:text-2xl lg:text-3xl font-body font-normal mt-3 text-white/85">
                免费漫画大全 · 全集免费在线阅读
              </span>
            </h1>
            <p className="text-white/70 text-sm md:text-base max-w-xl mx-auto mb-8 leading-relaxed">
              海量韩漫日漫国漫资源全覆盖，热门完结连载全部免费看，下拉式阅读体验超爽，追更不等待，好看的奇幻魔幻恋爱漫画尽在大全。
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
              <Link
                to="/category/all"
                className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-background-50 dark:text-foreground-950 font-medium rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer text-sm"
              >
                开始阅读 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
              <Link
                to="/category/hanman"
                className="w-full sm:w-auto px-8 py-3.5 bg-white/15 backdrop-blur-sm text-white border border-white/25 font-medium rounded-full hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer text-sm"
              >
                韩漫专区
              </Link>
              <Link
                to="/category/riman"
                className="w-full sm:w-auto px-8 py-3.5 bg-white/15 backdrop-blur-sm text-white border border-white/25 font-medium rounded-full hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer text-sm"
              >
                日漫专区
              </Link>
            </div>

            <div className="flex items-center justify-center gap-4 text-white/60 text-sm">
              <span className="flex items-center gap-1">
                <i className="ri-book-open-line"></i> 12,000+ 部漫画
              </span>
              <span className="w-1 h-1 rounded-full bg-white/30"></span>
              <span className="flex items-center gap-1">
                <i className="ri-user-smile-line"></i> 860万+ 读者
              </span>
              <span className="w-1 h-1 rounded-full bg-white/30"></span>
              <span className="flex items-center gap-1">
                <i className="ri-timer-line"></i> 每日更新
              </span>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-10 md:py-14 bg-background-50" aria-labelledby="category-heading">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 id="category-heading" className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                漫画分类
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer border border-background-200/60 hover:border-primary-200"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-lg text-accent-600 group-hover:text-primary-500 transition-colors`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-400">
                    {cat.count}部
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="py-10 md:py-14 bg-background-100/70" aria-labelledby="hot-heading">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
              <div>
                <h2 id="hot-heading" className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">
                  精选高分漫画，大家都在看的爆款
                </p>
              </div>
              <div className="flex items-center gap-1.5 bg-background-50 rounded-full p-1 border border-background-200/60">
                {[
                  { key: 'all', label: '全部' },
                  { key: 'guoman', label: '国漫' },
                  { key: 'hanman', label: '韩漫' },
                  { key: 'riman', label: '日漫' },
                  { key: 'danmei', label: '耽美' },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveCategoryTab(tab.key)}
                    className={`px-4 py-1.5 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      activeCategoryTab === tab.key
                        ? 'bg-primary-500 text-white dark:text-foreground-950 font-medium shadow-sm'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {filteredHotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="medium" />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-10 md:py-14 bg-background-50" aria-labelledby="latest-heading">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 id="latest-heading" className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500 mt-1">
                  实时追踪，追更不等待
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {latestUpdates.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group flex gap-3 p-3 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer border border-background-200/60"
                  data-product-shop
                >
                  <div className="w-16 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">
                      {comic.author}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 whitespace-nowrap">
                        {comic.category}
                      </span>
                      <span className="text-[10px] text-foreground-400 whitespace-nowrap">
                        {comic.latestChapter}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mt-1.5 text-[10px] text-foreground-400">
                      <i className="ri-time-line"></i>
                      <span>{comic.updateTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-8 py-3 bg-foreground-950 text-background-50 font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                查看更多更新 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Ranking Section */}
        <section className="py-10 md:py-14 bg-background-100/70" aria-labelledby="ranking-heading">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 id="ranking-heading" className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  热门排行
                </h2>
                <p className="text-sm text-foreground-500 mt-1">
                  根据阅读量和评分综合排名
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-0.5 bg-background-50 rounded-full p-1 border border-background-200/60">
                <button className="px-4 py-1.5 text-sm rounded-full bg-primary-500 text-white dark:text-foreground-950 font-medium shadow-sm whitespace-nowrap cursor-pointer">
                  人气榜
                </button>
                <button className="px-4 py-1.5 text-sm rounded-full text-foreground-600 hover:text-foreground-900 whitespace-nowrap cursor-pointer">
                  新作榜
                </button>
                <button className="px-4 py-1.5 text-sm rounded-full text-foreground-600 hover:text-foreground-900 whitespace-nowrap cursor-pointer">
                  完结榜
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {rankingComics.slice(0, 10).map((comic, index) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group flex items-center gap-4 p-3 rounded-xl bg-background-50 hover:bg-background-200/40 transition-colors cursor-pointer border border-background-200/60"
                  data-product-shop
                >
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full font-bold text-sm bg-accent-100 text-accent-700">
                    {index + 1}
                  </div>
                  <div className="w-12 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {comic.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                      <span>{comic.author}</span>
                      <span className="text-background-300">|</span>
                      <span className="text-accent-600">{comic.category}</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="flex items-center gap-1 text-accent-500 text-sm font-medium">
                      <i className="ri-star-fill text-xs"></i>
                      {comic.rating}
                    </div>
                    <div className="text-[10px] text-foreground-400 mt-0.5">{comic.views}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-20 overflow-hidden" aria-labelledby="cta-heading">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cozy%20reading%20atmosphere%20with%20soft%20ambient%20lighting%2C%20bookshelf%20background%20with%20warm%20golden%20and%20coral%20tones%2C%20comfortable%20reading%20nook%20vibe%2C%20artistic%20illustration%20with%20gentle%20bokeh%20effects%2C%20peaceful%20and%20inviting%20atmosphere&width=1920&height=600&seq=cta-bg-2026&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/60"></div>

          <div className="relative z-10 max-w-[700px] mx-auto px-4 md:px-6 text-center">
            <h2 id="cta-heading" className="text-2xl md:text-4xl font-heading font-bold text-white mb-4">
              即刻开启免费阅读之旅
            </h2>
            <p className="text-white/70 text-sm md:text-base mb-8 max-w-lg mx-auto leading-relaxed">
              妖精漫画网始终坚持<strong>完全免费</strong>，无需注册登录即可畅享海量漫画资源。下拉式阅读，沉浸式体验，追更不等待。
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-10 py-4 bg-accent-500 text-background-50 dark:text-foreground-950 font-bold text-base rounded-full hover:bg-accent-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              立即免费阅读 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      {/* Scroll to Top */}
      {scrollToTopVisible && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-40 w-11 h-11 flex items-center justify-center rounded-full bg-primary-500 text-white dark:text-foreground-950 shadow-lg hover:bg-primary-600 transition-all cursor-pointer animate-fade-in"
          aria-label="回到顶部"
        >
          <i className="ri-arrow-up-line text-lg"></i>
        </button>
      )}
    </div>
  );
}